import React, { useEffect, useState } from 'react';
import {
  Scale, Loader2, ShieldAlert, Package, RotateCcw, Percent,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { clientPortalService } from '../../services/clientPortal.service';
import { formatDay } from '../../lib/overOrder';

/**
 * Sold against ordered, per product — the same figures the bakery is looking
 * at, which is what makes the ceiling arguable rather than arbitrary. Only
 * guaranteed-returns accounts have returns to show.
 */
const Stat = ({ icon: Icon, label, value, tone = 'text-gray-900' }) => (
  <div className="rounded-lg border bg-white px-3 py-2">
    <p className="text-xs text-gray-500 flex items-center gap-1">
      <Icon className="h-3 w-3" /> {label}
    </p>
    <p className={`text-xl font-bold mt-0.5 ${tone}`}>{value}</p>
  </div>
);

export const SalesPerformanceCard = () => {
  const [weeks, setWeeks] = useState('12');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    (async () => {
      try {
        const res = await clientPortalService.getSalesPerformance(Number(weeks));
        if (active) setData(res);
      } catch {
        if (active) setData(null);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [weeks]);

  if (!loading && !data?.has_guaranteed_returns) return null;

  const totals = data?.totals;
  const products = data?.products || [];
  const target = data?.target_pct;

  return (
    <Card data-testid="sales-performance-card">
      <CardHeader className="flex flex-col gap-3 space-y-0 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div>
          <CardTitle className="text-lg flex items-center gap-2">
            <Scale className="h-5 w-5 text-amber-500" />
            Sold against ordered
          </CardTitle>
          <p className="text-sm text-gray-500 mt-1 max-w-xl">
            What you took and what sold, product by product — plus your best single delivery and
            the ceiling it sets. Unsold stock comes back to us, so these are the figures behind
            any order we question.
          </p>
        </div>
        <Select value={weeks} onValueChange={setWeeks}>
          <SelectTrigger className="w-full sm:w-36 sm:shrink-0" data-testid="sales-performance-weeks">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="4">Last 4 weeks</SelectItem>
            <SelectItem value="12">Last 12 weeks</SelectItem>
            <SelectItem value="26">Last 26 weeks</SelectItem>
            <SelectItem value="52">Last 52 weeks</SelectItem>
          </SelectContent>
        </Select>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-12" data-testid="sales-performance-loading">
            <Loader2 className="h-6 w-6 animate-spin text-amber-500" />
          </div>
        ) : !data ? (
          <p className="text-sm text-gray-500 py-8 text-center">
            We couldn't load this just now. Try again in a moment.
          </p>
        ) : (
          <div className="space-y-5">
            {data.gating?.active && (
              <div
                className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5"
                data-testid="sales-performance-gating"
              >
                <ShieldAlert className="h-4 w-4 text-amber-700 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-amber-900">
                    Orders above your best day are held for approval at the moment
                  </p>
                  <p className="text-xs text-amber-800/90 mt-0.5">
                    {data.gating.reason || 'Two consecutive months of low sell-through.'}
                    {data.gating.since ? ` In place since ${formatDay(data.gating.since)}.` : ''}
                    {' '}You can still place any order you like — we confirm it before the ordering deadline.
                  </p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Stat icon={Package} label="Taken" value={totals?.qty_ordered ?? 0} />
              <Stat icon={Package} label="Sold" value={totals?.qty_sold ?? 0} tone="text-green-700" />
              <Stat icon={RotateCcw} label="Came back" value={totals?.qty_returned ?? 0} tone="text-orange-700" />
              <Stat
                icon={Percent}
                label="Sell-through"
                value={totals?.sell_through_pct != null ? `${totals.sell_through_pct}%` : '—'}
                tone={
                  totals?.sell_through_pct != null && target != null && totals.sell_through_pct < target
                    ? 'text-orange-700'
                    : 'text-green-700'
                }
              />
            </div>

            {products.length === 0 ? (
              <p className="text-sm text-gray-500 py-6 text-center" data-testid="sales-performance-empty">
                Nothing settled in this period yet. Figures appear once a delivery's returns
                window has closed.
              </p>
            ) : (
              <>
                {/* Phone: one block per product. A seven-column table is not
                    something anyone reads on a 390px screen. */}
                <ul className="space-y-3 sm:hidden">
                  {products.map((p) => {
                    const under = p.sell_through_pct != null && target != null && p.sell_through_pct < target;
                    return (
                      <li
                        key={p.product_id}
                        className="rounded-lg border bg-white px-3 py-2.5"
                        data-testid={`sales-performance-card-${p.product_id}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <p className="font-medium text-gray-900 text-sm">{p.product_name}</p>
                          {p.sell_through_pct == null ? (
                            <span className="text-gray-400 text-sm">—</span>
                          ) : (
                            <Badge className={`shrink-0 ${under ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800'}`}>
                              {p.sell_through_pct}% sold
                            </Badge>
                          )}
                        </div>
                        <dl className="mt-2 grid grid-cols-3 gap-2 text-xs">
                          <div>
                            <dt className="text-gray-500">Taken</dt>
                            <dd className="font-semibold text-gray-900 tabular-nums">{p.qty_ordered}</dd>
                          </div>
                          <div>
                            <dt className="text-gray-500">Sold</dt>
                            <dd className="font-semibold text-green-700 tabular-nums">{p.qty_sold}</dd>
                          </div>
                          <div>
                            <dt className="text-gray-500">Came back</dt>
                            <dd className="font-semibold text-orange-700 tabular-nums">{p.qty_returned}</dd>
                          </div>
                          <div>
                            <dt className="text-gray-500">Best day</dt>
                            <dd className="font-semibold text-gray-900 tabular-nums">{p.peak_sold ?? '—'}</dd>
                          </div>
                          <div>
                            <dt className="text-gray-500">Ceiling</dt>
                            <dd className="font-semibold text-gray-900 tabular-nums">{p.cap_qty ?? '—'}</dd>
                          </div>
                          <div>
                            <dt className="text-gray-500">We'd suggest</dt>
                            <dd className="font-semibold text-gray-900 tabular-nums">{p.suggested_qty ?? '—'}</dd>
                          </div>
                        </dl>
                      </li>
                    );
                  })}
                </ul>

                <div className="hidden sm:block overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-xs uppercase text-gray-500">
                        <th className="py-2 pr-2 text-left">Product</th>
                        <th className="py-2 px-2 text-right">Taken</th>
                        <th className="py-2 px-2 text-right">Sold</th>
                        <th className="py-2 px-2 text-right">Back</th>
                        <th className="py-2 px-2 text-right">Sell-through</th>
                        <th className="py-2 px-2 text-right">Best day</th>
                        <th className="py-2 pl-2 text-right">Ceiling</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {products.map((p) => {
                        const under = p.sell_through_pct != null && target != null && p.sell_through_pct < target;
                        return (
                          <tr key={p.product_id} data-testid={`sales-performance-row-${p.product_id}`}>
                            <td className="py-2 pr-2">
                              <p className="font-medium text-gray-900">{p.product_name}</p>
                              <p className="text-xs text-gray-500">
                                {p.deliveries} deliver{p.deliveries === 1 ? 'y' : 'ies'}
                                {p.suggested_qty != null ? ` · we'd suggest ${p.suggested_qty}` : ''}
                              </p>
                            </td>
                            <td className="py-2 px-2 text-right tabular-nums">{p.qty_ordered}</td>
                            <td className="py-2 px-2 text-right tabular-nums text-green-700">{p.qty_sold}</td>
                            <td className="py-2 px-2 text-right tabular-nums text-orange-700">{p.qty_returned}</td>
                            <td className="py-2 px-2 text-right">
                              {p.sell_through_pct == null ? (
                                <span className="text-gray-400">—</span>
                              ) : (
                                <Badge className={under ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800'}>
                                  {p.sell_through_pct}%
                                </Badge>
                              )}
                            </td>
                            <td className="py-2 px-2 text-right tabular-nums">{p.peak_sold ?? '—'}</td>
                            <td className="py-2 pl-2 text-right tabular-nums font-medium">{p.cap_qty ?? '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <p className="text-xs text-gray-500">
                  The ceiling is your best single delivery plus an allowance. A product with too few
                  recorded deliveries has no ceiling at all, and we target {target ?? 85}% sell-through.
                </p>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SalesPerformanceCard;
